import { Chip, Tooltip } from '@assembly-js/design-system';
import { Autocomplete } from '@mui/material';
import { useFormikContext } from 'formik';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon } from 'src/components/icons';
import { TagIcon } from 'src/components/icons/TagIcon';
import { VisibilityRuleType } from 'src/constants';
import { useFeatureFlags } from 'src/context/flagsContext';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
import { BaseTextField } from 'src/legacy/components/TextField';
import { CustomVisibilitySchema } from 'src/utils/app_visibility';
import { isCategoricalColor } from 'src/utils/ColorUtils';
import { ensureUnreachable } from 'src/utils/common_utils';

import { VisibilityRecipient } from './AppVisibilityRule';

type CustomFieldId = string;

type MultiSelectDropdownOption = Omit<MultiSelectOption, 'order' | 'color'> & {
  color: string;
};

export type RuleField = {
  id: VisibilityRuleType.Client | VisibilityRuleType.Company | CustomFieldId;
  options: MultiSelectDropdownOption[];
};

function isVisibilityRuleType(type: any): type is VisibilityRuleType {
  return Object.values(VisibilityRuleType).includes(type);
}

function getSelectedFieldName(type: VisibilityRuleType) {
  switch (type) {
    case VisibilityRuleType.Client:
      return i18next.t(($) => $.client, { count: 2 });
    case VisibilityRuleType.Company:
      return i18next.t(($) => $.company, { count: 2 });
    case VisibilityRuleType.CustomField:
      return 'Tags';
    case VisibilityRuleType.NoVisibility:
      return '';
    default:
      return ensureUnreachable(type);
  }
}

export const VisibilityRecipientSelector: React.FC<{
  ruleIndex: number;
  recipients: VisibilityRecipient[];
  clients: {
    id: string;
    fields: { givenName: string; familyName: string; fallbackColor: string };
  }[];
  selectedFieldValues: RuleField;
  selectedRuleType: VisibilityRuleType;
  onRecipientSelected: React.ComponentProps<
    typeof RecipientSelector
  >['onRecipientSelected'];
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  /**
   * Settings' visibility modal renders this field joined to the "Client"/"Is"
   * cells to its left as one bordered row, so only its right corners round.
   * The publish popover stacks it as its own full-width row, so it needs all
   * four corners rounded.
   */
  standaloneField?: boolean;
}> = ({
  ruleIndex,
  recipients,
  clients,
  onRecipientSelected,
  error,
  helperText,
  selectedRuleType,
  selectedFieldValues,
  placeholder,
  standaloneField = false,
}) => {
  const { t } = useTranslation();
  const count = recipients.length;
  const chipRef = React.useRef<HTMLDivElement>(null);
  const [chipWidth, setChipWidth] = React.useState(0);
  const { values, touched, setFieldValue, errors } =
    useFormikContext<CustomVisibilitySchema>();
  const { EnableFeatureAppVisibility: isAppVisibilityFeatureEnabled } =
    useFeatureFlags();

  React.useEffect(() => {
    if (chipRef.current) {
      setChipWidth(chipRef.current.offsetWidth);
    } else {
      setChipWidth(0);
    }
  }, [count, recipients]);

  const uniqueClients = useMemo(() => {
    const seen = new Set<string>();
    return recipients.filter((r) => {
      if (seen.has(r.clientId)) return false;
      seen.add(r.clientId);
      return true;
    });
  }, [recipients]);

  const clientDetails = useMemo(
    () =>
      uniqueClients
        .map((r) => clients.find((c) => c.id === r.clientId))
        .filter(Boolean),
    [uniqueClients, clients],
  );

  const uniqueCount = clientDetails.length;
  const chipLabel =
    uniqueCount === 1
      ? `${clientDetails.at(0)?.fields.givenName} ${clientDetails.at(0)?.fields.familyName}`
      : `${uniqueCount} ${t(($) => $.client, { count: uniqueCount })}`;

  const tooltipText =
    clientDetails.length === 1
      ? chipLabel
      : clientDetails
          .slice(0, 5)
          .map((c) => `${c?.fields.givenName} ${c?.fields.familyName}`)
          .join(', ') + (clientDetails.length > 5 ? '...' : '');

  const handleFieldValueChange = (_: unknown, value: unknown) => {
    setFieldValue(`visibilityConfig.${ruleIndex}.values`, value);
  };

  const selectedOptionsRenderer = (options: unknown[]): React.ReactNode => {
    const filteredOptions = options
      .map((option) => selectedFieldValues.options.find((x) => x.id === option))
      .filter(Boolean);

    // id will be a GUID when it's a custom field
    const optionsName = isVisibilityRuleType(selectedFieldValues.id)
      ? getSelectedFieldName(selectedFieldValues.id)
      : 'Tags';

    const getTooltipContent = (
      selectedOptions: MultiSelectDropdownOption[],
    ) => {
      let content = '';
      // if only one option is selected, show the label
      if (selectedOptions.length === 1) {
        content = selectedOptions.at(0)?.label || '';
      }

      // if more than one option is selected, show the number of options
      if (selectedOptions.length > 1) {
        content =
          selectedOptions
            .slice(0, 5)
            .map((x) => x.label)
            .join(', ') + (selectedOptions.length > 5 ? '...' : '');
      }

      return <span className="text-sm">{content}</span>;
    };

    return (
      <Tooltip content={getTooltipContent(filteredOptions)} position="bottom">
        <Chip
          key={optionsName}
          variant="outlined"
          className="max-w-[120px] [&>p]:truncate [&>p]:max-w-full"
          label={
            filteredOptions.length > 1
              ? `${filteredOptions.length} ${optionsName}`
              : filteredOptions.at(0)?.label || ''
          }
          prefixIcon={
            <div className="flex">
              {filteredOptions
                .filter((x) => x.color !== 'none')
                .slice(0, 3)
                .map((x) => (
                  <div
                    className="relative [&:nth-child(2)]:left-[-2px] [&:nth-child(3)]:left-[-4px] bottom-[0.5px]"
                    key={x.id}
                  >
                    <TagIcon
                      className="text-[6px]"
                      style={{
                        fill: isCategoricalColor(x.color)
                          ? `var(--${x.color}-primary)`
                          : x.color,
                      }}
                    />
                  </div>
                ))}
            </div>
          }
        />
      </Tooltip>
    );
  };

  const errorContainer = errors.visibilityConfig?.[ruleIndex];

  const optionDropdownHelperText =
    !!touched.visibilityConfig?.[ruleIndex]?.values &&
    typeof errorContainer !== 'string' &&
    errorContainer?.values;

  return selectedRuleType === VisibilityRuleType.Client &&
    isAppVisibilityFeatureEnabled ? (
    <div className="relative w-full">
      {count > 0 && (
        <div
          ref={chipRef}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 z-10"
        >
          <Tooltip
            content={<span className="text-sm">{tooltipText}</span>}
            position="bottom"
          >
            <Chip
              variant="outlined"
              className="max-w-[120px] [&>p]:truncate [&>p]:max-w-full flex items-baseline"
              label={chipLabel}
              prefixIcon={
                <div className="flex">
                  {clientDetails
                    .filter((c) => c?.fields.fallbackColor)
                    .slice(0, 3)
                    .map((c, i) => (
                      <div
                        key={c?.id || i}
                        className="relative [&:nth-child(2)]:left-[-2px] [&:nth-child(3)]:left-[-4px]"
                      >
                        <TagIcon
                          className="text-[6px]"
                          style={{
                            fill: isCategoricalColor(
                              c?.fields.fallbackColor || '',
                            )
                              ? `var(--${c?.fields.fallbackColor}-primary)`
                              : c?.fields.fallbackColor,
                          }}
                        />
                      </div>
                    ))}
                </div>
              }
            />
          </Tooltip>
        </div>
      )}
      <div className="visibility-recipient-compact">
        <style
          dangerouslySetInnerHTML={{
            __html: `.visibility-recipient-compact [class*="cop-py-[5px]"] { display: none !important; }`,
          }}
        />
        <RecipientSelector
          name={`visibilityConfig.${ruleIndex}.valueItems`}
          recipientTypes={['client']}
          onRecipientSelected={onRecipientSelected}
          selectedRecipients={recipients}
          ignoreFilterClientsByCompany
          keepSelectedInList
          listClassName="data-[selected=true]:!bg-[#F1F3F8]"
          styles={{
            control: (base: Record<string, unknown>) => ({
              ...base,
              minHeight: 36,
              borderRadius: standaloneField ? '4px' : '0 4px 4px 0',
              borderColor: '#DFE1E4',
              backgroundColor: '#fff',
              '&:focus-within': {
                borderColor: '#1A1A1A',
              },
            }),
            valueContainer: (base: any) => ({
              ...base,
              paddingLeft: chipWidth > 0 ? chipWidth : 0,
            }),
          }}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
        />
      </div>
      <div className="pointer-events-none absolute right-[11px] top-[47%] -translate-y-1/2">
        <ChevronDownIcon className="h-[15px] w-[15px] text-[#6B6F76]" />
      </div>
    </div>
  ) : (
    <Autocomplete
      classes={{
        listbox:
          "py-1.5 [&_.MuiAutocomplete-option[aria-selected='true']]:!bg-[#F1F3F8]",
        paper:
          'border border-solid border-[#DFE1E4] shadow-[0px_9px_24px_rgba(0,0,0,0.12)] [&_.MuiAutocomplete-groupUl_.MuiAutocomplete-option]:pl-4',
        inputRoot:
          '[&_.MuiAutocomplete-popupIndicator]:!pt-0 [&_.MuiAutocomplete-endAdornment]:mr-0.5 [&_.MuiAutocomplete-endAdornment]:bg-white',
      }}
      multiple
      limitTags={1}
      id="tags-outlined"
      options={selectedFieldValues.options.map((x) => x.id)}
      renderTags={selectedOptionsRenderer}
      getOptionLabel={(optionId) =>
        selectedFieldValues.options.find((x) => x.id === optionId)?.label || ''
      }
      value={values.visibilityConfig?.[ruleIndex].values ?? []}
      popupIcon={
        <ChevronDownIcon className="h-[15px] w-[15px] text-[#6B6F76]" />
      }
      onChange={handleFieldValueChange}
      renderInput={(params) => (
        <BaseTextField
          {...params}
          variant="outlined"
          sizeVariant="medium"
          name={`visibilityConfig.${ruleIndex}.values`}
          placeholder={placeholder}
          isPlaceholder={!values.visibilityConfig?.[ruleIndex]?.values?.length}
          className="[&_.MuiOutlinedInput-input]:text-base [&_.MuiOutlinedInput-input]:leading-[22px]"
          error={
            !!touched.visibilityConfig?.[ruleIndex]?.values &&
            !!optionDropdownHelperText
          }
          helperText={optionDropdownHelperText}
        />
      )}
      disableClearable
    />
  );
};
